// Map a Sefaria link's (linkType, category) to an HONEST, ANCHOR-AGNOSTIC
// relationship label + an internal strength tier.
//
// ACCURACY LOCK: the same source link is rendered both under a verse AND under
// another source (digital-sefer, arbitrary nesting depth). The label must
// describe the LINK TYPE as Sefaria defines it and stay true regardless of how
// deep the reader is. So labels say "this" -- NEVER "this verse" -- because the
// thing on the other side of "this" is not always a verse.
//
// linkType is the link's `type` field (Sefaria often leaves it ""). category is
// the Sefaria top-level category. Resolution order:
//   1. non-empty + KNOWN linkType        -> linkType row (most specific signal)
//   2. EMPTY linkType ("")               -> category row
//   3. non-empty + UNKNOWN linkType      -> generic weak fallback (see below)
//   4. unknown category (when empty)     -> generic weak fallback
//
// SUBTLETY (key accuracy guard): a non-empty BUT UNKNOWN linkType does NOT fall
// through to the category row. Sefaria handed us a type we do not recognize, so
// we must not turn around and assert a category-shaped claim ("comments on
// this") about it. We degrade to the deliberately-weak generic instead. The
// category row is consulted ONLY when linkType is exactly "".

export type RelStrength = "direct" | "related" | "thematic";

export interface Relationship {
  label: string;
  strength: RelStrength;
}

// The deliberately-weak generic: returned when we have no honest, specific claim
// to make. Never a confident guess.
const GENERIC: Relationship = { label: "linked source", strength: "thematic" };

// Known link-type rows. Keyed by lowercased linkType. Describes the LINK TYPE
// itself, so it is true at any nesting depth.
const LINK_TYPE_ROWS: Readonly<Record<string, Relationship>> = {
  "commentary": { label: "comments on this", strength: "direct" },
  "parshanut": { label: "explains this passage", strength: "direct" },
  "targum": { label: "translates this", strength: "direct" },
  "sifrei mitzvot": { label: "codifies this", strength: "direct" },
  "midrash": { label: "expounds this", strength: "direct" },
  "quotation": { label: "quotes this", strength: "related" },
  "quotation_auto": { label: "cites this", strength: "related" },
  "quotation_auto_tanakh": { label: "cites this", strength: "related" },
  "reference": { label: "references this", strength: "thematic" },
  "related": { label: "related to this", strength: "thematic" },
  "essay": { label: "discusses this", strength: "thematic" },
};

// Category fallback rows. Keyed by lowercased category. Consulted ONLY when
// linkType is exactly "" (empty). A category is a coarser signal than an
// explicit type, so several rows are softer than their linkType cousins.
const CATEGORY_ROWS: Readonly<Record<string, Relationship>> = {
  "commentary": { label: "comments on this", strength: "direct" },
  "targum": { label: "translates this", strength: "direct" },
  "midrash": { label: "expounds this", strength: "related" },
  "mishnah": { label: "cites this", strength: "related" },
  "talmud": { label: "cites this", strength: "related" },
  "tosefta": { label: "cites this", strength: "related" },
  "halakhah": { label: "codifies this", strength: "direct" },
  "responsa": { label: "discusses this", strength: "thematic" },
  "kabbalah": { label: "interprets this", strength: "thematic" },
  "chasidut": { label: "teaches on this", strength: "thematic" },
  "musar": { label: "draws a lesson from this", strength: "thematic" },
  "jewish thought": { label: "reflects on this", strength: "thematic" },
  "philosophy": { label: "discusses this idea", strength: "thematic" },
  "tanakh": { label: "a related passage", strength: "thematic" },
};

function normalize(s: string): string {
  return s.trim().toLowerCase();
}

export function getRelationship(linkType: string, category: string): Relationship {
  const type = normalize(linkType);
  const cat = normalize(category);

  // 1. Non-empty linkType: trust it if known.
  if (type !== "") {
    const row = LINK_TYPE_ROWS[type];
    if (row) return row;
    // Non-empty but UNKNOWN type: degrade to generic. Do NOT consult the
    // category row -- asserting a category claim about an unrecognized type
    // would be a dishonest guess.
    return GENERIC;
  }

  // 2. Empty linkType: fall back to the category row.
  const catRow = CATEGORY_ROWS[cat];
  if (catRow) return catRow;

  // 3. Unknown category: generic weak fallback.
  return GENERIC;
}

// Ordering rank: direct (strongest) -> 0, related -> 1, thematic (weakest) -> 2.
export function strengthRank(s: RelStrength): number {
  switch (s) {
    case "direct":
      return 0;
    case "related":
      return 1;
    case "thematic":
      return 2;
  }
}
